import { v1 as uuidv1 } from 'uuid';
import type { LanguageKey } from '@softgames/localization';
import { GAME_ECONOMY_SETTINGS } from 'game/GameConstants';
import { type LevelData } from '../../game/level/LevelData';
import { getLevelHash, getTotalLevels } from '../../../levels';
import { type LegacyAppState } from './legacy/LegacyAppState';
import type { LivesState } from './lives/LivesState';
import { LIVES_MAX } from './lives/LivesConstants';
import type { BoosterTypeMap } from '../../game/boosters/types';
import { BoosterType } from '../../game/boosters/types';
import type { LevelTaskTallies } from '../../game/tasks/types';
import { FeatureCooldownType } from './cooldowns/types';
import type { EntryPointPayloadRecord } from '../entryPoints/types';
import type { DioramaState } from './diorama/dioramaState';
import type { TournamentSaveData } from '../tournaments/types';
import type { PiggyBankState } from '../../gameEvents/piggyBank/PiggyBankState';
import type { WinStreakState } from '../../gameEvents/winStreak/WinStreakState';
import type { RaceEventState } from '../../gameEvents/raceEvent/types';
import type { LuckyWheelState } from '../../gameEvents/luckyWheel/types';
import type { OfferSystemData } from '../../libs/offerSystem/OfferSystemHistory';
import type { InitializedSeasonPassConfig, LastSeasonPassInfo } from '../../gameEvents/seasonPass/types';
import type { MacroBalancingState } from '../../game/level/difficulty/types';
import type { CollectionEventAppState } from '../../gameEvents/collectionEvent/types';

export interface AppState {
  appVer: string;
  horizontalLayout: boolean;
  language: LanguageKey | null;
  sessionCount: number;
  userFirstLogin: number;
  trackingId: string;
  levelIndex: number;
  levelStarted: boolean;
  /** levelScore is deprecated, only used for converting progress to levelTaskTallies */
  levelScore: number;
  levelTaskTallies: LevelTaskTallies | null;
  levelReviveUsed: number;
  levelAttempts: number;
  /** Unique id set when a level is first started */
  levelAttemptId: string | null;
  levelData: Partial<LevelData> | null;
  /** To keep track of how many levels were last available to the user. */
  totalLevels: number;
  levelHash: string | null;
  coins: number;
  lives: LivesState;
  boosters: BoosterTypeMap<number>;
  sfxVolume: number;
  musicVolume: number;
  state?: LegacyAppState | null;
  splitTest: Record<string, number>;
  splitTestData: {
    hs57noAdTimer?: number;
    hs715interstitialsTier1?: boolean;
    hs715lastAd?: number;
  };
  leaderboard: {
    pool: number;
    startLevel: number;
    joinedAt: number;
  };
  entryPayloads: EntryPointPayloadRecord[];
  featureCooldowns: Partial<Record<FeatureCooldownType, number>>;
  diorama: DioramaState;
  piggyBank: PiggyBankState | null;
  iap: {
    offerSystemData: OfferSystemData;
  };
  tournaments: TournamentSaveData[];
  shortcut: {
    addedAt: number | null;
  };
  dailyReward: {
    day: number;
    freeRewardOpened: boolean;
    adRewardsOpened: number;
  };
  economyRebalanceDate?: number | null;
  winStreakActiveEvent: WinStreakState | null;
  winStreakEventLastEndTime: number;
  raceEvent: RaceEventState | null;
  raceEventLastEndTime: number;
  trackingOfferShow: Record<string, string>;
  freeShopGift: {
    claimedAt: number;
    giftOpened: boolean;
    giftShown: boolean;
  };
  seasonPass: InitializedSeasonPassConfig | null;
  lastSeasonPass: LastSeasonPassInfo | null;
  seasonPassFinishedAmount: number;
  macroBalancing: MacroBalancingState | null;
  collectionEvent: CollectionEventAppState;
  lastInterstitialAdTime: number;
  geoLocation: {
    tier1: boolean;
    fetchTime: number;
  } | null;
  luckyWheel: LuckyWheelState;
  versus: {
    shownIntro: boolean;
    difficulty: number;
    lossesInRow: number;
    afterFirstWin: boolean;
  } | null;
  /**
   * Device identifier used for native app launched event.
   * Specifically for native mobile platforms.(Android, iOS)
   */
  deviceIdentifier: string | null;
  cheatEnabled: boolean;
  userId: string;
  purchasedProducts: string[];
  updatedAt: number;
  revision: number;
}

export type StateFromAppStateKeys<K extends keyof AppState = keyof AppState> = {
  [P in K]: AppState[P];
};

export const INITIAL_STATE: AppState = {
  appVer: APP_VERSION,
  horizontalLayout: false,
  language: null,
  sessionCount: 0,
  userFirstLogin: Date.now(),
  trackingId: uuidv1(),
  levelIndex: 0,
  levelStarted: false,
  levelReviveUsed: 0,
  levelScore: 0,
  levelTaskTallies: null,
  levelAttempts: 0,
  levelAttemptId: uuidv1(),
  levelData: null,
  totalLevels: getTotalLevels(),
  levelHash: getLevelHash(0),
  coins: GAME_ECONOMY_SETTINGS.initialCoinBalance,
  lives: {
    count: LIVES_MAX,
    lastLifeAt: Date.now(),
    unlimitedUntil: 0,
  },
  boosters: {
    [BoosterType.Hammer]: 3,
    [BoosterType.Move]: 3,
    [BoosterType.Redraw]: 2,
    [BoosterType.Undo]: 2,
  },
  sfxVolume: 100,
  musicVolume: 100,
  state: null,
  splitTest: {},
  splitTestData: {},
  leaderboard: {
    pool: 0,
    startLevel: 1,
    joinedAt: Date.now(),
  },
  entryPayloads: [],
  featureCooldowns: {
    [FeatureCooldownType.LifeRequest]: 0,
  },
  diorama: {
    dioramaIndex: 0,
    tilesAmount: 0,
  },
  piggyBank: null,
  iap: {
    offerSystemData: {},
  },
  tournaments: [],
  shortcut: {
    addedAt: null,
  },
  dailyReward: {
    day: 0,
    freeRewardOpened: false,
    adRewardsOpened: 0,
  },
  economyRebalanceDate: null,
  winStreakActiveEvent: null,
  winStreakEventLastEndTime: 0,
  raceEvent: null,
  raceEventLastEndTime: 0,
  trackingOfferShow: {},
  freeShopGift: {
    claimedAt: 0,
    giftOpened: false,
    giftShown: false,
  },
  seasonPass: null,
  lastSeasonPass: null,
  seasonPassFinishedAmount: 0,
  macroBalancing: null,
  collectionEvent: {
    currentEvent: null,
    tokensCollectedHistory: [],
  },
  lastInterstitialAdTime: 0,
  geoLocation: null,
  luckyWheel: {
    lastFreeSpinTime: 0,
    lastAdSpinTime: 0,
    rewardedFreeSpins: 0,
    bonusFreeSpinTime: 0,
    adSpinCount: 0,
    lastWonRewardIndex: null,
    luckyWheelShown: false,
  },
  versus: null,
  deviceIdentifier: null,
  cheatEnabled: false,
  userId: uuidv1(),
  purchasedProducts: [],
  updatedAt: 0,
  revision: 1,
};
